/**
 * The open month so far, from bank alerts. Provisional by definition: alerts are
 * classified with the same rules a statement uses, then counted with the same maths
 * as a closed month, so the two agree once the statement arrives.
 * Only accounts that send alerts are covered; the caller must say which are not.
 */
import type { Account, Category, Month, ProvisionalTransaction, Transaction } from '../data/types';
import { classify } from './classify';
import { isPersonalSpend, monthMoney, type MonthMoney } from './decisions';
import { isInvestmentAccount } from '../data/types';
import type { PayeeRule } from './payee';

export interface MonthSoFar {
  money: MonthMoney;
  rows: number;
  /** The alerts as classified transactions, newest first. */
  txs: Transaction[];
  lastSeen: string | null;
  /** Personal spending ceiling minus personal spend so far. Null without a ceiling. */
  ceilingLeft: number | null;
  /** ceilingLeft spread over the days left in the month, today included. */
  perDayLeft: number | null;
  daysLeft: number;
  topCategories: { name: string; amount: number }[];
  /** Transactional NGN accounts with no alert this month (not set up, or not arriving yet). */
  notCovered: string[];
  /** Banks that actually sent alerts this month. */
  banksSeen: string[];
  /** Naira taken back out of investments (redemptions), so it is not mistaken for income. */
  fromInvestments: number;
}

export function asTransactions(rows: ProvisionalTransaction[], categories: Category[], rules: PayeeRule[]): Transaction[] {
  return rows.filter((r) => r.status !== 'matched').map((r, index) => {
    const c = classify({ date: r.tx_date, description: r.description, amount: r.amount, direction: r.direction, balance: null, amountSource: 'parsed', index },
      r.currency, categories, rules);
    return {
      id: r.id, tx_date: r.tx_date, description: r.description, amount: r.amount, direction: r.direction, currency: r.currency,
      classification: c.classification, category_name: c.category?.name ?? c.classification, account_label: r.account_label,
      needs_review: false, category_type: c.category?.category_type ?? (c.missingSystemCategory?.type ?? null),
      is_internal_transfer: c.is_internal_transfer, is_business_funding: c.is_business_funding,
    };
  });
}

export function monthSoFar(rows: ProvisionalTransaction[], categories: Category[], rules: PayeeRule[], plan: Month | null,
  accounts: Account[], now = new Date()): MonthSoFar {
  const txs = asTransactions(rows, categories, rules);
  const money = monthMoney(txs);
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const daysLeft = Math.max(1, last - now.getDate() + 1);
  const cap = plan?.personal_spend_cap ?? null;
  const ceilingLeft = cap === null ? null : cap - money.personal;
  const by = new Map<string, number>();
  for (const t of txs) {
    if (!isPersonalSpend(t)) continue;
    const k = t.category_name ?? 'Other';
    by.set(k, (by.get(k) ?? 0) + t.amount);
  }
  const covered = new Set(rows.map((r) => r.account_id));
  return {
    money,
    rows: txs.length,
    txs,
    lastSeen: rows.reduce<string | null>((m, r) => (m === null || r.occurred_at > m ? r.occurred_at : m), null),
    ceilingLeft,
    perDayLeft: ceilingLeft === null ? null : Math.max(0, ceilingLeft) / daysLeft,
    daysLeft,
    topCategories: [...by].map(([name, amount]) => ({ name, amount })).sort((a, b) => b.amount - a.amount).slice(0, 4),
    banksSeen: [...new Set(rows.map((r) => r.bank))].sort(),
    fromInvestments: txs.filter((t) => t.direction === 'credit' && t.currency === 'NGN' && t.category_type === 'investment' && !t.is_internal_transfer)
      .reduce((a, t) => a + t.amount, 0),
    notCovered: accounts
      .filter((a) => a.currency === 'NGN' && !covered.has(a.id) && !isInvestmentAccount(a))
      .map((a) => a.name),
  };
}
